import React, { useEffect, useState } from "react";
import { Row, Col, Card, CardBody, Input } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { loadingTrue, loadingFalse } from "../Reducer/loaderSlice";
import Handler from "../Helpers/Handler";
import { toast } from "react-toastify";
import KpiCard from "./KpiCard";
import TotalCard from "./TotalCard";
import DatePicker from "react-datepicker";
import NetSalesAreaChart from "./SalesChart";

const Dashboard = () => {

  const dispatch = useDispatch();
  const user = useSelector(state => state.customer.customer);

  /* ================= FILTER ================= */
  const [period, setPeriod] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  /* ================= KPI ================= */
  const [kpis, setKpis] = useState({
    total_sales: 0,
    net_sales: 0,
    collections: 0,
    outstanding: 0
  });

  /* ================= TOTALS ================= */
  const [salesTotals, setSalesTotals] = useState({});
  const [plTotals, setPlTotals] = useState({});
  const [inventoryTotals, setInventoryTotals] = useState({});
  const [overallOutstanding, setOverallOutstanding] = useState(0); // Machan, idhu thaan overall value-kaga
  const [salesChart, setSalesChart] = useState([]);

  /* =================================================
      LOAD OVERALL OUTSTANDING (ONLY ORG_ID)
  ================================================= */
  const loadOverallOutstanding = async () => {
    if (!user?.org_id) return;
    
    // Inga query filter edhum illa, just org_id vachu overall edukurom
    const res = await Handler({ 
        method: "get", 
        url: `/reports/customer_report.php?org_id=${user.org_id}` 
    });

    if (res?.success && res.data?.rows) {
      const total = res.data.rows.reduce((acc, curr) => acc + Number(curr.outstanding || 0), 0);
      setOverallOutstanding(total);
    }
  };

  /* =================================================
     BUILD COMMON QUERY (FOR OTHER DATA)
  ================================================= */
  const buildQuery = () => {
    if (!user?.org_id) return null;

    let q = `org_id=${user.org_id}&outlet_id=${user.outlet_id}`;

    if (period === "custom") {
      if (!fromDate || !toDate) {
        toast.error("Select from & to date");
        return null;
      }
      q += `&date_from=${fromDate.toISOString().split("T")[0]}`;
      q += `&date_to=${toDate.toISOString().split("T")[0]}`;
      return q;
    }

    if (period) {
      q += `&period=${period}`;
    }

    return q;
  };

  /* =================================================
     LOAD DASHBOARD (ALL APIS)
  ================================================= */
  const load = async () => {
    const query = buildQuery();
    if (!query) return;

    dispatch(loadingTrue());

    try {
      const [dash, sales, pl, inventory] = await Promise.all([
        Handler({ method: "get", url: `/reports/dashboard.php?${query}` }),
        Handler({ method: "get", url: `/reports/sales_summary.php?${query}` }),
        Handler({ method: "get", url: `/reports/profit_loss.php?${query}` }),
        Handler({ method: "get", url: `/reports/inventory_report.php?${query}` })
      ]);

      if (dash?.success) setKpis(dash.data?.kpis || {});

      if (sales?.success) {
        setSalesTotals(sales.data?.totals || {});
        setSalesChart(
          (sales.data?.rows || []).map(r => ({
            time: r.sdate,
            sales: Number(r.sale_bills || 0),
            collections: Number(r.collections || 0),
            net_sales: Number(r.sales_amount || r.collections || 0)
          }))
        );
      }

      if (pl?.success) setPlTotals(pl.data?.totals || {});
      if (inventory?.success) setInventoryTotals(inventory.data?.totals || {});

    } catch (e) {
      toast.error("Dashboard load failed");
    }
    dispatch(loadingFalse());
  };

  useEffect(() => {
    load();
    loadOverallOutstanding(); // Initial load-la overall value fetch panrom
  }, [period, fromDate, toDate, user?.org_id]);

  /* ================= UI ================= */
  return (
    <>
      <h4 className="fw-bold mb-1">Billing Dashboard</h4>
      <p className="text-muted mb-4">Overall business performance</p>

      {/* ================= FILTER ================= */}
      <Row className="mb-3 align-items-end">
        <Col md={3}>
          <label className="small text-muted">Period</label>
          <Input type="select" value={period} onChange={e => setPeriod(e.target.value)}>
            <option value="">All</option>
            <option value="today">Today</option>
            <option value="last_3_days">Last 3 Days</option>
            <option value="last_month">Last Month</option>
            <option value="last_3_months">Last 3 Months</option>
            <option value="last_year">Last Year</option>
            <option value="custom">Custom Range</option>
          </Input>
        </Col>

        {period === "custom" && (
          <>
            <Col md={3}>
              <label className="small text-muted w-100">From Date</label>
              <DatePicker
                selected={fromDate}
                onChange={setFromDate}
                dateFormat="yyyy-MM-dd"
                className="form-control"
                maxDate={new Date()}
              />
            </Col>
            <Col md={3}>
              <label className="small text-muted w-100">To Date</label>
              <DatePicker
                selected={toDate}
                onChange={setToDate}
                dateFormat="yyyy-MM-dd"
                className="form-control"
                minDate={fromDate}
                maxDate={new Date()}
              />
            </Col>
          </>
        )}
      </Row>

      {/* ================= KPI ================= */}
      <Row className="mb-4">
        <Col md={3}><KpiCard title="Total Sales" value={`₹ ${Number(kpis.total_sales).toFixed(2)}`} bg="orange" /></Col>
        <Col md={3}><KpiCard title="Net Sales" value={`₹ ${Number(kpis.net_sales).toFixed(2)}`} bg="dark" /></Col>
        <Col md={3}><KpiCard title="Collections" value={`₹ ${Number(kpis.collections).toFixed(2)}`} bg="green" /></Col>
        
        {/* Machan, inga overall outstanding logic updated */}
        <Col md={3}>
            <KpiCard 
                title="Overall Outstanding" 
                value={`₹ ${overallOutstanding.toFixed(2)}`} 
                bg="red" 
            />
        </Col>

        <Col md={3} className="mt-3"><KpiCard title="Loyalty Redeemed" value={`₹ ${Number(kpis.loyalty_redeemed).toFixed(2)}`} bg="red" /></Col>
        <Col md={3} className="mt-3"><KpiCard title="Total Discount" value={kpis.discount} bg="black" /></Col>
        <Col md={3} className="mt-3"><KpiCard title="Return Amount" value={`₹ ${Number(kpis.return_amount).toFixed(2)}`} bg="lavender" /></Col>
        <Col md={3} className="mt-3"><KpiCard title="Return Counts" value={kpis.return_count} bg="blue" /></Col>
      </Row>

      {/* ================= SALES TREND ================= */}
      <Row className="mb-4">
        <Col md={12}>
          <Card>
            <CardBody>
              <h6 className="dashboard-section-title">
                <i className="bx bx-line-chart"></i> Sales Trend
              </h6>
              <NetSalesAreaChart data={salesChart} />
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* ================= SALES TOTALS ================= */}
      <Row className="mb-4">
        <Col md={4}><TotalCard title="Items Sold" value={salesTotals?.items_qty || 0} icon="bx bx-package" color="green" /></Col>
        <Col md={4}><TotalCard title="Gross Profit" value={plTotals?.gross_profit} icon="bx bx-bar-chart-alt" color="green" /></Col>
        <Col md={4}><TotalCard title="Purchase Cost" value={`₹ ${Number(plTotals?.cost_amount || 0).toFixed(2)}`} icon="bx bx-credit-card" color="red" /></Col>
      </Row>

      {/* ================= PROFIT & LOSS / INVENTORY ================= */}
      <Row className="mb-4">
        <Col md={4}><TotalCard title="Collections" value={`₹ ${Number(plTotals?.collections || 0).toFixed(2)}`} icon="bx bx-wallet" color="blue" /></Col>
        <Col md={4}><TotalCard title="Products" value={inventoryTotals?.products || 0} icon="bx bx-cube" color="red" /></Col>
        <Col md={4}><TotalCard title="Sold Value" value={`₹ ${Number(inventoryTotals?.sold_value || 0).toFixed(2)}`} icon="bx bx-rupee" color="blue" /></Col>
      </Row>
    </>
  );
};

export default Dashboard;